import { useState, useEffect, useCallback } from 'react';
import { getCommissions, formatIQD, formatUSD } from '../utils/api';
import toast from 'react-hot-toast';
import { DollarSign, Calendar, Users, ArrowUpRight, TrendingUp, Search, Download } from 'lucide-react';

export default function CommissionsPage() {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [fromDate, setFromDate] = useState('');
	const [toDate, setToDate] = useState('');

	const loadData = useCallback(async () => {
		setLoading(true);
		try {
			const params = {};
			if (fromDate) params.from_date = fromDate;
			if (toDate) params.to_date = toDate;
			const result = await getCommissions(params);
			setData(result);
		} catch (err) {
			toast.error('❌ خطأ في تحميل تقارير العمولات');
		}
		setLoading(false);
	}, [fromDate, toDate]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	const totalEarned = Array.isArray(data) ? data.reduce((sum, item) => sum + (item.total_earned_iqd || 0), 0) : 0;
	const totalPaid = Array.isArray(data) ? data.reduce((sum, item) => sum + (item.paid_earned_iqd || 0), 0) : 0;
	const totalPending = totalEarned - totalPaid;

	const btnClass = "flex items-center gap-2 px-4 py-2 bg-white dark:bg-dark-deeper border border-gray-200 dark:border-white/10 rounded-xl text-sm font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:bg-white/5 dark:hover:bg-white/5 transition-all shadow-sm";

	return (
		<div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
			{/* Header */}
			<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
				<div>
					<h1 className="text-2xl font-black text-dark dark:text-gray-100 flex items-center gap-3">
						<div className="p-2 bg-emerald-100 rounded-xl text-emerald-600">
							<TrendingUp size={24} />
						</div>
						تقارير العمولات (الحوافز)
					</h1>
					<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">تتبع أداء الموظفين والعمولات المستحقة والمدفوعة.</p>
				</div>
				<div className="flex gap-2">
					<button onClick={loadData} className={btnClass}><Search size={16} /> تحديث البيانات</button>
				</div>
			</div>

			{/* Stats Cards */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
				<div className="bg-white dark:bg-dark-deeper p-5 rounded-2xl border border-gray-100 dark:border-white/5 flex items-center justify-between shadow-sm">
					<div>
						<p className="text-sm text-gray-500 dark:text-gray-400 font-bold mb-1">إجمالي العمولات</p>
						<h3 className="text-2xl font-black text-dark dark:text-gray-100">{formatIQD(totalEarned)}</h3>
						<span className="text-xs text-info font-bold mt-1 block">بناءً على الفواتير المكتملة</span>
					</div>
					<div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-500 flex items-center justify-center"><DollarSign size={24} /></div>
				</div>
				<div className="bg-white dark:bg-dark-deeper p-5 rounded-2xl border border-gray-100 dark:border-white/5 flex items-center justify-between shadow-sm">
					<div>
						<p className="text-sm text-gray-500 dark:text-gray-400 font-bold mb-1">العمولات المحصلة (مدفوعة)</p>
						<h3 className="text-2xl font-black text-dark dark:text-gray-100">{formatIQD(totalPaid)}</h3>
						<span className="text-xs text-success font-bold mt-1 block">تم دفعها للموظفين</span>
					</div>
					<div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center"><ArrowUpRight size={24} /></div>
				</div>
				<div className="bg-white dark:bg-dark-deeper p-5 rounded-2xl border border-gray-100 dark:border-white/5 flex items-center justify-between shadow-sm">
					<div>
						<p className="text-sm text-gray-500 dark:text-gray-400 font-bold mb-1">العمولات المعلقة</p>
						<h3 className="text-2xl font-black text-dark dark:text-gray-100">{formatIQD(totalPending)}</h3>
						<span className="text-xs text-warning font-bold mt-1 block">بانتظار الصرف</span>
					</div>
					<div className="w-12 h-12 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center"><Clock size={24} /></div>
				</div>
			</div>

			{/* Filters */}
			<div className="flex gap-4 items-center bg-gray-50 dark:bg-white/5/50 p-3 rounded-2xl border border-gray-100 dark:border-white/5">
				<div className="flex items-center gap-3 bg-white dark:bg-dark-deeper px-3 py-2 rounded-xl border border-gray-200 dark:border-white/10">
					<Calendar size={16} className="text-gray-400" />
					<input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="text-sm outline-none bg-transparent" />
					<span className="text-gray-300">←</span>
					<input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="text-sm outline-none bg-transparent" />
				</div>
				{(fromDate || toDate) && (
					<button onClick={() => { setFromDate(''); setToDate(''); }} className="text-xs font-bold text-danger hover:underline">إلغاء الفلتر</button>
				)}
			</div>

			{/* Table */}
			<div className="bg-white dark:bg-dark-deeper rounded-2xl border border-gray-100 dark:border-white/5 shadow-sm overflow-hidden">
				{loading ? (
					<div className="flex justify-center items-center h-40">
						<div className="w-8 h-8 border-4 border-gray-200 dark:border-white/10 border-t-primary rounded-full animate-spin"></div>
					</div>
				) : data.length === 0 ? (
					<div className="p-16 text-center">
						<div className="w-16 h-16 bg-gray-50 dark:bg-white/5 rounded-2xl border border-dashed border-gray-200 dark:border-white/10 flex items-center justify-center mx-auto mb-4 text-gray-400">
							<Users size={28} />
						</div>
						<h3 className="text-lg font-bold text-dark dark:text-gray-100 mb-1">لا توجد سجلات عمولات لهذه الفترة</h3>
						<p className="text-sm text-gray-500 dark:text-gray-400">سيتم عرض العمولات هنا عند تسجيل فواتير محالة من موظفين.</p>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full text-right">
							<thead>
								<tr className="bg-gray-50 dark:bg-white/5 border-b border-gray-100 dark:border-white/5 text-gray-500 dark:text-gray-400">
									<th className="px-6 py-4 text-xs font-bold">الموظف</th>
									<th className="px-6 py-4 text-xs font-bold">عدد الإحالات</th>
									<th className="px-6 py-4 text-xs font-bold">إجمالي المبيعات</th>
									<th className="px-6 py-4 text-xs font-bold">العمولة المستحقة</th>
									<th className="px-6 py-4 text-xs font-bold">العمولة المدفوعة</th>
									<th className="px-6 py-4 text-xs font-bold">المتبقي</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-50">
								{data.map(item => (
									<tr key={item.employee_id} className="hover:bg-gray-50 dark:bg-white/5 dark:hover:bg-white/5/50 transition-colors">
										<td className="px-6 py-4">
											<div className="flex items-center gap-3">
												<div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center font-black text-sm">
													{item.employee_name.charAt(0)}
												</div>
												<span className="font-bold text-dark dark:text-gray-100">{item.employee_name}</span>
											</div>
										</td>
										<td className="px-6 py-4 text-sm font-medium text-gray-600 dark:text-gray-300 font-mono">{item.invoice_count}</td>
										<td className="px-6 py-4 text-sm font-bold text-dark dark:text-gray-100">{formatIQD(item.total_sales_iqd)}</td>
										<td className="px-6 py-4 text-sm font-black text-emerald-600">{formatIQD(item.total_earned_iqd)}</td>
										<td className="px-6 py-4 text-sm font-medium text-gray-600 dark:text-gray-300">{formatIQD(item.paid_earned_iqd)}</td>
										<td className="px-6 py-4">
											<span className={`text-sm font-bold ${item.total_earned_iqd - item.paid_earned_iqd > 0 ? 'text-warning' : 'text-success'}`}>
												{formatIQD(item.total_earned_iqd - item.paid_earned_iqd)}
											</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
}

function Clock(props) {
	return (
		<svg
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="12" cy="12" r="10" />
			<polyline points="12 6 12 12 16 14" />
		</svg>
	);
}
